// src/pages/TicketNew.jsx
// Rediseño visual aplicado 2026-05-13. Lógica preservada 1:1.
import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import ImageUploader from '../components/ImageUploader';


// ── Iconos SVG inline ────────────────────────────────────────────────────────
const Icon = ({ children, size = 16, stroke = 1.75 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke="currentColor" strokeWidth={stroke}
         strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        {children}
    </svg>
);
const I = {
    back:     <Icon><path d="M19 12H5" /><path d="m12 19-7-7 7-7" /></Icon>,
    plus:     <Icon><path d="M12 5v14" /><path d="M5 12h14" /></Icon>,
    x:        <Icon size={14}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></Icon>,
    send:     <Icon><path d="m22 2-7 20-4-9-9-4 20-7Z" /></Icon>,
    calendar: <Icon><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></Icon>,
    package:  <Icon><path d="M16.5 9.4 7.55 4.24" /><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" /></Icon>,
    info:     <Icon size={14}><circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" /></Icon>,
    alert:    <Icon size={14}><path d="M12 9v4" /><path d="M12 17h.01" /><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></Icon>,
};


// ── Scoped CSS ───────────────────────────────────────────────────────────────
const TN_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

.tn-root {
    --tn-accent: var(--brand-700, #0a4aa8);
    --tn-accent-hover: var(--brand-800, #07357f);
    --tn-accent-light: #dbeafe;
    --tn-accent-ring: rgba(10, 74, 168, .12);
    --tn-radius-lg: 14px;
    --tn-radius-md: 10px;
    --tn-radius-sm: 8px;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    -webkit-font-smoothing: antialiased;
    background: var(--c-bg);
    min-height: 100vh;
    padding: 20px 20px 60px;
    color: var(--c-text-1);
}
.tn-root * { box-sizing: border-box; }
.tn-root h1 { letter-spacing: -0.01em; margin: 0; }

.tn-container { max-width: 760px; margin: 0 auto; }

/* Breadcrumb */
.tn-breadcrumb {
    display: flex; align-items: center; gap: 10px;
    font-size: 12px; color: var(--c-text-3); margin-bottom: 14px;
}
.tn-breadcrumb a {
    color: var(--c-text-3); text-decoration: none;
    display: inline-flex; align-items: center; gap: 6px;
    padding: 4px 8px; border-radius: 6px; transition: all .15s;
}
.tn-breadcrumb a:hover { background: var(--c-msg-bg); color: var(--c-text-1); }

/* Header */
.tn-header { margin-bottom: 18px; }
.tn-header h1 {
    font-size: 24px; font-weight: 700; color: var(--c-text-1); margin: 0 0 4px;
}
.tn-header p { color: var(--c-text-3); font-size: 13.5px; margin: 0; }

/* Card */
.tn-card {
    background: var(--c-card);
    border: 1px solid var(--c-border);
    border-radius: var(--tn-radius-lg);
    box-shadow: 0 1px 2px rgba(15,23,42,.04), 0 2px 8px rgba(15,23,42,.04);
    padding: 24px 28px;
}

/* Form groups */
.tn-group { margin-bottom: 18px; }
.tn-label {
    display: block;
    font-size: 12px; font-weight: 600; letter-spacing: 0.3px;
    color: var(--c-text-2); margin-bottom: 6px;
}
.tn-label .required { color: #dc2626; margin-left: 2px; }
.tn-label .optional { font-weight: 400; color: var(--c-text-4); margin-left: 4px; }
.tn-hint {
    font-size: 12px; color: var(--c-text-4); margin: 6px 0 0;
    display: flex; align-items: center; gap: 6px;
}
.tn-error {
    font-size: 12px; color: #dc2626; margin: 6px 0 0;
    display: flex; align-items: center; gap: 6px;
}

/* Inputs */
.tn-input, .tn-select, .tn-textarea {
    width: 100%; padding: 9px 12px;
    font-size: 14px;
    border: 1px solid var(--c-border);
    border-radius: var(--tn-radius-sm);
    background: var(--c-input-bg);
    color: var(--c-text-1);
    font-family: inherit; outline: none;
    transition: border-color .15s, box-shadow .15s, background .15s;
}
.tn-input:focus, .tn-select:focus, .tn-textarea:focus {
    border-color: var(--tn-accent);
    box-shadow: 0 0 0 3px var(--tn-accent-ring);
    background: var(--c-card);
}
.tn-textarea { resize: vertical; min-height: 110px; line-height: 1.55; }
.tn-input-narrow { max-width: 220px; }
.tn-input-narrower { max-width: 180px; }

/* Material rows */
.tn-material-list { display: flex; flex-direction: column; gap: 8px; }
.tn-material-row {
    display: flex; align-items: center; gap: 8px;
    border: 1px solid var(--c-border); border-radius: var(--tn-radius-sm);
    padding: 6px 10px;
    background: var(--c-input-bg);
    transition: border-color .15s;
}
.tn-material-row:focus-within { border-color: var(--tn-accent); }
.tn-material-row .tn-select-small {
    flex: 2 1 0; padding: 6px 10px; font-size: 13px;
    border: 1px solid var(--c-border); border-radius: 6px;
    background: var(--c-card); color: var(--c-text-1);
    font-family: inherit; outline: none;
}
.tn-material-row .tn-select-small:focus { border-color: var(--tn-accent); }
.tn-material-row .tn-count {
    flex: 0 0 80px; padding: 6px 10px; font-size: 13px;
    border: 1px solid var(--c-border); border-radius: 6px;
    background: var(--c-card); color: var(--c-text-1);
    font-family: inherit; outline: none;
}
.tn-material-row .tn-count:focus { border-color: var(--tn-accent); }
.tn-material-row .remove {
    background: transparent; border: 1px solid var(--c-border);
    color: #dc2626; padding: 5px 8px; border-radius: 6px;
    cursor: pointer; display: inline-flex; align-items: center;
    transition: all .15s; flex-shrink: 0;
}
.tn-material-row .remove:hover:not(:disabled) {
    background: var(--c-priority-urgent-bg); border-color: #fecaca;
}
.tn-material-row .remove:disabled { opacity: 0.4; cursor: not-allowed; }

.tn-material-foot {
    display: flex; justify-content: space-between; align-items: center;
    margin-top: 10px; gap: 12px; flex-wrap: wrap;
}
.tn-add-material {
    background: transparent; color: var(--tn-accent);
    border: 1px dashed var(--tn-accent); border-radius: var(--tn-radius-sm);
    padding: 6px 12px; font-size: 12px; font-weight: 600;
    cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
    transition: all .15s; font-family: inherit;
}
.tn-add-material:hover { background: var(--tn-accent-light); }
.tn-estimate {
    font-size: 12px; color: var(--c-text-3);
    background: var(--c-msg-bg); padding: 4px 10px; border-radius: 999px;
    display: inline-flex; align-items: center; gap: 6px;
}
.tn-estimate strong { color: var(--c-text-1); font-weight: 700; }

/* Buttons */
.tn-actions {
    display: flex; gap: 10px;
    padding-top: 18px; border-top: 1px solid var(--c-card-header-border);
    margin-top: 4px;
}
.tn-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    border: 1px solid transparent;
    border-radius: var(--tn-radius-sm);
    padding: 10px 22px;
    font-size: 14px; font-weight: 600;
    cursor: pointer; transition: all .15s;
    font-family: inherit;
}
.tn-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.tn-btn-primary {
    background: var(--tn-accent); color: #fff;
    border-color: var(--tn-accent);
    box-shadow: 0 1px 3px rgba(10,74,168,.25);
}
.tn-btn-primary:hover:not(:disabled) {
    background: var(--tn-accent-hover);
    border-color: var(--tn-accent-hover);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(10,74,168,.25);
}
.tn-btn-ghost {
    background: transparent; color: var(--c-text-2);
    border-color: var(--c-border);
}
.tn-btn-ghost:hover:not(:disabled) {
    background: var(--c-msg-bg); border-color: var(--c-text-3);
}

/* Responsive */
@media (max-width: 600px) {
    .tn-root { padding: 14px 12px 60px; }
    .tn-card { padding: 18px 18px; }
    .tn-actions { flex-direction: column-reverse; }
    .tn-actions .tn-btn { width: 100%; }
    /* 16px evita el zoom de iOS al enfocar un campo */
    .tn-input, .tn-select, .tn-textarea, .tn-root select, .tn-root input, .tn-root textarea { font-size: 16px; }
}

/* Fade-in */
@keyframes tn-fade {
    from { opacity: 0; transform: translateY(6px); }
    to   { opacity: 1; transform: none; }
}
.tn-fade { animation: tn-fade .3s ease-out both; }
`;


export default function TicketNew() {
    const navigate = useNavigate();

    const [title,       setTitle]       = useState('');
    const [description, setDescription] = useState('');
    const [priority,    setPriority]    = useState('medium');
    const [department,  setDepartment]  = useState('');
    const [depts,       setDepts]       = useState([]);
    const [loadingDepts, setLoadingDepts] = useState(true);

    const [selectedCase,   setSelectedCase]   = useState('');
    const [attachments,    setAttachments]    = useState([]);
    const [materialsCount, setMaterialsCount] = useState('');
    const [materialItems,  setMaterialItems]  = useState([{ type: '', count: 1 }]);
    const [deliveryDate,   setDeliveryDate]   = useState('');
    const [creating,       setCreating]       = useState(false);

    const currentDept         = depts.find(d => d._id === department) || null;
    const isDeliveryDateDept  = currentDept?.slaMode === 'deliveryDate';
    const isMaterialTypeDept  = currentDept?.slaMode === 'materialType';
    const activeMaterialTypes = (currentDept?.materialTypes || []).filter(t => t.active !== false);
    const typeHoursByName     = new Map(activeMaterialTypes.map(t => [t.name, Number(t.slaHours) || 0]));
    const estimatedHours      = isMaterialTypeDept
        ? materialItems.reduce((acc, it) => acc + (typeHoursByName.get(it.type) || 0) * (Number(it.count) || 0), 0)
        : 0;

    useEffect(() => {
        let alive = true;
        setLoadingDepts(true);
        api.get('/departments')
            .then(({ data }) => { if (alive) setDepts(Array.isArray(data) ? data : []); })
            .catch(() => toast.error('No se pudieron cargar los departamentos'))
            .finally(() => alive && setLoadingDepts(false));
        return () => { alive = false; };
    }, []);

    const handleDeptChange = (e) => {
        const id = e.target.value;
        setDepartment(id);
        setSelectedCase('');
        setMaterialsCount('');
        setMaterialItems([{ type: '', count: 1 }]);
        setDeliveryDate('');
        const dept = depts.find(d => d._id === id);
        if (dept?.ticketTemplate) setDescription(dept.ticketTemplate);
        else setDescription('');
        setTitle('');
    };

    const updateMaterialItem = (i, patch) =>
        setMaterialItems(prev => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
    const addMaterialItem    = () => setMaterialItems(prev => [...prev, { type: '', count: 1 }]);
    const removeMaterialItem = i => setMaterialItems(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);

    const handleCaseChange = (e) => {
        const val = e.target.value;
        setSelectedCase(val);
        setTitle(val);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!department)         return toast.warn('Selecciona un departamento');
        if (currentDept?.ticketCases?.length > 0 && !selectedCase)
                                 return toast.warn('Selecciona un caso');
        if (!title.trim())       return toast.warn('El título es obligatorio');
        if (!description.trim()) return toast.warn('La descripción es obligatoria');
        if (isDeliveryDateDept && !deliveryDate) return toast.warn('Indica la fecha de entrega requerida');
        if (isMaterialTypeDept) {
            const cleanItems = materialItems.filter(it => it.type && Number(it.count) >= 1);
            if (cleanItems.length === 0)  return toast.warn('Agrega al menos un tipo de material');
            if (!deliveryDate)            return toast.warn('Indica la fecha de entrega comprometida');
        }

        setCreating(true);
        try {
            const payload = { title, description, department, priority, attachments };
            if (selectedCase)                                payload.case           = selectedCase;
            if (isDeliveryDateDept && materialsCount !== '') payload.materialsCount = Number(materialsCount);
            if (isDeliveryDateDept && deliveryDate)          payload.deliveryDate   = deliveryDate;
            if (isMaterialTypeDept) {
                payload.materialItems = materialItems
                    .filter(it => it.type && Number(it.count) >= 1)
                    .map(it => ({ type: it.type, count: Number(it.count) }));
                payload.deliveryDate  = deliveryDate;
            }
            const { data } = await api.post('/tickets', payload);
            toast.success(`Ticket creado: ${data.folio || data.title}`, { autoClose: 2500 });
            navigate(`/tickets/${data._id}`);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al crear ticket', { autoClose: 4000 });
        } finally { setCreating(false); }
    };

    // Render jerárquico de departamentos: padres alfabético + hijos indentados
    const renderDeptOptions = () => {
        const roots    = depts.filter(d => !d.parent).sort((a, b) => a.name.localeCompare(b.name));
        const orphans  = depts.filter(d => d.parent && !roots.some(r => String(r._id) === String(d.parent?._id || d.parent)));
        const out = [];
        for (const r of roots) {
            out.push(<option key={r._id} value={r._id}>{r.name}</option>);
            const kids = depts
                .filter(d => String(d.parent?._id || d.parent || '') === String(r._id))
                .sort((a, b) => a.name.localeCompare(b.name));
            for (const k of kids) {
                out.push(<option key={k._id} value={k._id}>{'    ↳ ' + k.name}</option>);
            }
        }
        for (const o of orphans) out.push(<option key={o._id} value={o._id}>{o.name}</option>);
        return out;
    };

    const minDate = new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString().slice(0, 10);

    return (
        <>
            <style>{TN_CSS}</style>
            <div className="tn-root">
                <div className="tn-container tn-fade">

                    {/* Breadcrumb */}
                    <div className="tn-breadcrumb">
                        <Link to="/tickets">{I.back} Tickets</Link>
                        <span style={{ color: 'var(--c-text-4)' }}>/</span>
                        <span style={{ color: 'var(--c-text-2)' }}>Nuevo</span>
                    </div>

                    {/* Header */}
                    <div className="tn-header">
                        <h1>Nuevo ticket</h1>
                        <p>Completa los datos para enviar tu solicitud al equipo correspondiente.</p>
                    </div>

                    <div className="tn-card">
                        <form onSubmit={handleSubmit}>

                            {/* Departamento */}
                            <div className="tn-group">
                                <label className="tn-label">
                                    Departamento <span className="required">*</span>
                                </label>
                                <select
                                    className="tn-select"
                                    value={department}
                                    onChange={handleDeptChange}
                                    required
                                    disabled={loadingDepts}
                                >
                                    <option value="">— Selecciona un departamento —</option>
                                    {renderDeptOptions()}
                                </select>
                                {loadingDepts && (
                                    <p className="tn-hint">
                                        <Spinner size="sm" animation="border" /> Cargando departamentos…
                                    </p>
                                )}
                            </div>

                            {/* Caso / Tipo o Título */}
                            {currentDept?.ticketCases?.length > 0 ? (
                                <div className="tn-group">
                                    <label className="tn-label">
                                        Caso / Tipo <span className="required">*</span>
                                    </label>
                                    <select
                                        className="tn-select"
                                        value={selectedCase}
                                        onChange={handleCaseChange}
                                        required
                                    >
                                        <option value="">— Selecciona un caso —</option>
                                        {currentDept.ticketCases.map((c, i) => (
                                            <option key={i} value={c}>{c}</option>
                                        ))}
                                    </select>
                                </div>
                            ) : (
                                <div className="tn-group">
                                    <label className="tn-label">
                                        Título <span className="required">*</span>
                                    </label>
                                    <input
                                        className="tn-input"
                                        value={title}
                                        onChange={e => setTitle(e.target.value)}
                                        placeholder="Ej. No puedo acceder al sistema"
                                        required
                                    />
                                </div>
                            )}

                            {/* Descripción */}
                            <div className="tn-group">
                                <label className="tn-label">
                                    Descripción <span className="required">*</span>
                                </label>
                                <textarea
                                    className="tn-textarea"
                                    rows={5}
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                    placeholder="Describe el problema con detalle…"
                                    required
                                />
                            </div>

                            {/* Materiales (modo materialType) */}
                            {isMaterialTypeDept && (
                                <>
                                    <div className="tn-group">
                                        <label className="tn-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            {I.package} Materiales solicitados <span className="required">*</span>
                                        </label>

                                        {activeMaterialTypes.length === 0 ? (
                                            <p className="tn-error">
                                                {I.alert}
                                                Este departamento aún no tiene tipos de material configurados. Pide al líder que los defina.
                                            </p>
                                        ) : (
                                            <div className="tn-material-list">
                                                {materialItems.map((it, i) => (
                                                    <div key={i} className="tn-material-row">
                                                        <select
                                                            className="tn-select-small"
                                                            value={it.type}
                                                            onChange={e => updateMaterialItem(i, { type: e.target.value })}
                                                            required
                                                        >
                                                            <option value="">— Tipo —</option>
                                                            {activeMaterialTypes.map((t, j) => (
                                                                <option key={j} value={t.name}>
                                                                    {t.name} ({t.slaHours} h/u)
                                                                </option>
                                                            ))}
                                                        </select>
                                                        <input
                                                            className="tn-count"
                                                            type="number" min={1}
                                                            value={it.count}
                                                            onChange={e => updateMaterialItem(i, { count: Number(e.target.value) })}
                                                            placeholder="Cant."
                                                            required
                                                        />
                                                        <button
                                                            type="button"
                                                            className="remove"
                                                            onClick={() => removeMaterialItem(i)}
                                                            disabled={materialItems.length === 1}
                                                            title="Quitar"
                                                        >{I.x}</button>
                                                    </div>
                                                ))}
                                                <div className="tn-material-foot">
                                                    <button
                                                        type="button"
                                                        className="tn-add-material"
                                                        onClick={addMaterialItem}
                                                    >
                                                        {I.plus} Agregar otro material
                                                    </button>
                                                    {estimatedHours > 0 && (
                                                        <span className="tn-estimate">
                                                            Estimado interno: <strong>{estimatedHours} h</strong>
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="tn-group">
                                        <label className="tn-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            {I.calendar} Fecha de entrega comprometida <span className="required">*</span>
                                        </label>
                                        <input
                                            className="tn-input tn-input-narrow"
                                            type="date"
                                            value={deliveryDate}
                                            onChange={e => setDeliveryDate(e.target.value)}
                                            min={minDate}
                                            required
                                        />
                                        <p className="tn-hint">
                                            {I.info}
                                            Esta es la fecha en que el ticket vence (compromiso con el solicitante).
                                        </p>
                                    </div>
                                </>
                            )}

                            {/* Campos modo deliveryDate (Diseño Instruccional legacy) */}
                            {isDeliveryDateDept && (
                                <>
                                    <div className="tn-group">
                                        <label className="tn-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            {I.package} Número de materiales solicitados <span className="required">*</span>
                                        </label>
                                        <input
                                            className="tn-input tn-input-narrower"
                                            type="number"
                                            min={1}
                                            value={materialsCount}
                                            onChange={e => setMaterialsCount(e.target.value)}
                                            placeholder="Ej. 3"
                                            required
                                        />
                                        <p className="tn-hint">
                                            {I.info}
                                            Indica cuántos materiales distintos incluye esta solicitud.
                                        </p>
                                    </div>

                                    <div className="tn-group">
                                        <label className="tn-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            {I.calendar} Fecha de entrega requerida <span className="required">*</span>
                                        </label>
                                        <input
                                            className="tn-input tn-input-narrow"
                                            type="date"
                                            value={deliveryDate}
                                            onChange={e => setDeliveryDate(e.target.value)}
                                            min={minDate}
                                            required
                                        />
                                        <p className="tn-hint">
                                            {I.info}
                                            ¿Para cuándo necesitas que esté listo?
                                        </p>
                                    </div>
                                </>
                            )}

                            {/* Prioridad — solo deptos en modo SLA por prioridad */}
                            {!isDeliveryDateDept && !isMaterialTypeDept && (
                                <div className="tn-group">
                                    <label className="tn-label">Prioridad</label>
                                    <select
                                        className="tn-select"
                                        value={priority}
                                        onChange={e => setPriority(e.target.value)}
                                    >
                                        <option value="low">Baja</option>
                                        <option value="medium">Media</option>
                                        <option value="high">Alta</option>
                                        <option value="urgent">Urgente</option>
                                    </select>
                                </div>
                            )}

                            {/* Adjuntos */}
                            <div className="tn-group">
                                <label className="tn-label">
                                    Adjuntos <span className="optional">(opcional)</span>
                                </label>
                                <ImageUploader
                                    urls={attachments}
                                    onChange={setAttachments}
                                    departmentId={department}
                                    allowExtendedUploads={!!currentDept?.allowExtendedUploads}
                                    globalDrop
                                />
                            </div>

                            {/* Acciones */}
                            <div className="tn-actions">
                                <button
                                    type="submit"
                                    className="tn-btn tn-btn-primary"
                                    disabled={creating}
                                >
                                    {creating ? <Spinner size="sm" animation="border" /> : <>{I.send} Crear ticket</>}
                                </button>
                                <button
                                    type="button"
                                    className="tn-btn tn-btn-ghost"
                                    onClick={() => navigate(-1)}
                                >
                                    Cancelar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
}
